import { baseApi, getQueryArgs } from 'store/api'
import { ANALYTICS_BASE_HOST_NAME } from 'gatsby-env-variables'
import { ApiResponse, getFile } from '../http'
import { transformStatsErrorResponse } from '../analytics/utils/transformStatsErrorResponse'
import omit from 'lodash/omit'
import type { RootState } from 'store/rootReducer'
import {
  addQuery,
  deleteLogs,
  startSessionTimer,
  updateEventSourceHandler,
} from 'store/activityLog'
import {
  ActivityLogParams,
  AuthTokenResponse,
  HistoricalQueriesResponse,
  RealtimeActivityLogParams,
  DeleteActivityLogParams,
} from 'store/api/activityLog/activityLog.interface'
import generateQueryParams from '../analytics/utils/generateQueryParams'

const AUTH_TOKEN_PATH = '/v2/auth/token'
const ACTIVITY_LOG_PATH = '/v2/activity-log'
const ACTIVITY_LOG_CSV_PATH = '/v2/activity-log/csv'
const ACTIVITY_LOG_REALTIME_PATH = '/v2/activity-log/realtime'

export const activityLogApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getAuthToken: builder.query({
      query: (params: { region: string }) => {
        return getQueryArgs(
          `https://${params.region}.${ANALYTICS_BASE_HOST_NAME}${AUTH_TOKEN_PATH}`,
        )
      },
      transformResponse: (response: ApiResponse<AuthTokenResponse>) => response.body,
      transformErrorResponse: transformStatsErrorResponse,
    }),

    getActivityLog: builder.query({
      query: (params: ActivityLogParams) => {
        const queryParams = generateQueryParams(omit(params, 'region'))
        return getQueryArgs(
          `https://${params.region}.${ANALYTICS_BASE_HOST_NAME}${ACTIVITY_LOG_PATH}?${queryParams}`,
        )
      },
      transformResponse: (response: ApiResponse<HistoricalQueriesResponse>) => response.body,
      transformErrorResponse: transformStatsErrorResponse,
    }),

    getActivityLogRealtime: builder.query({
      query: (params: RealtimeActivityLogParams) => {
        return getQueryArgs(
          `https://${params.region}.${ANALYTICS_BASE_HOST_NAME}${AUTH_TOKEN_PATH}`,
        )
      },
      async onCacheEntryAdded(
        { region, ...params },
        { dispatch, getState, updateCachedData, cacheDataLoaded },
      ) {
        try {
          const eventSourceHandler = (getState() as RootState).activityLog?.resetEventSource

          if (eventSourceHandler) {
            eventSourceHandler()
            dispatch(deleteLogs())
          }

          const { data } = await cacheDataLoaded
          const url = new URL(
            `https://${region}.${ANALYTICS_BASE_HOST_NAME}${ACTIVITY_LOG_REALTIME_PATH}`,
          )
          const baseParams = generateQueryParams(omit(params as ActivityLogParams, 'endTime'))
          url.search = baseParams
          const token = (data as ApiResponse<AuthTokenResponse>)?.body?.token
          if (token) {
            url.search += (url.search ? '&' : '') + `authToken=${encodeURIComponent(token)}`
          }

          const eventSrc = new EventSource(url.toString())

          if (eventSrc) {
            eventSrc.onmessage = event => {
              const payload = JSON.parse(event.data)
              updateCachedData(() => {
                dispatch(addQuery(payload))
              })
            }

            if (!eventSourceHandler) {
              dispatch(startSessionTimer())
            }

            dispatch(
              updateEventSourceHandler(() => {
                if (eventSrc.OPEN) {
                  eventSrc.close()
                }
              }),
            )
          }
        } catch {}
      },
      transformErrorResponse: transformStatsErrorResponse,
    }),

    deleteActivityLog: builder.mutation({
      query: (params: DeleteActivityLogParams) => {
        const queryParams = new URLSearchParams()

        if (params.endpointId !== undefined) {
          queryParams.append('endpointId', params.endpointId)
        }

        if (params.clientId !== undefined) {
          if (Array.isArray(params.clientId)) {
            params.clientId.forEach(id => queryParams.append('clientId[]', id))
          } else {
            queryParams.append('clientId', params.clientId)
          }
        }

        return {
          url: `https://${params.region}.${ANALYTICS_BASE_HOST_NAME}${ACTIVITY_LOG_PATH}?${queryParams}`,
          method: 'DELETE',
        }
      },
      transformErrorResponse: transformStatsErrorResponse,
    }),
  }),
})

// CSV export bypassing the cache since we do not need to store the exported csv
export const getActivityLogAsCSV = (region: string, queryString: string): Promise<Response> => {
  return getFile(
    `${ACTIVITY_LOG_CSV_PATH}?${queryString}`,
    undefined,
    `https://${region}.${ANALYTICS_BASE_HOST_NAME}`,
  )
}

export const {
  useGetAuthTokenQuery,
  useLazyGetAuthTokenQuery,
  useGetActivityLogQuery,
  useLazyGetActivityLogQuery,
  useGetActivityLogRealtimeQuery,
  useLazyGetActivityLogRealtimeQuery,
  useDeleteActivityLogMutation,
} = activityLogApi
